import React, { useContext, useEffect } from "react";
import { Box } from "rizzui/box";
import { Text } from "rizzui/typography";
import FormBox from "./fromBox";
import { Controller, useForm } from "react-hook-form";
import { Input } from "rizzui/input";
import { Textarea } from "rizzui/textarea";
import { Button } from "rizzui/button";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { ThemeContext } from "../../../context/themeContext";
import cn from "../../../lib/utils/class-name";
import { Radio } from "rizzui/radio";
import { useModal } from "../../../hooks/useModal";
import ImportMultipleFiles from "../../../component/cards/importMultipleFile";
import { inputClassName, labelClassName, textAreaClassName } from "./first";
import { AuthContext } from "../../../context/authContext";
import axios from "axios";
import { baseUrl } from "../../account/login";
import { useNavigate } from "react-router";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import CryptoJS from "crypto-js";
import { useColorStyle } from "../../../hooks/useColorStyle";
import { Tooltip } from "rizzui/tooltip";
import { BsFillInfoCircleFill } from "react-icons/bs";
import { ActionIcon } from "rizzui/action-icon";
import BinIcon from "../../../assets/bin";

export const projectFieldValidation = {
  required: {
    value: true,
    message: "is required",
  },
};

export default function Third({ data }) {
  const { page, setPage, campaign, setCurrentId } = data;
  const [showDropdown, setShowDropdown] = React.useState(true);

  const {
    handleSubmit,
    register,
    control,
    watch,
    getValues,
    reset,
    formState: { errors },
  } = useForm({});
  const { theme } = useContext(ThemeContext);
  const { openModal } = useModal();
  const navigate = useNavigate();

  const { token } = useContext(AuthContext);
  const AuthStr = `Bearer ${token}`;
  const key = process.env.REACT_APP_SECRET_KEY;
  const campaign_id = sessionStorage.getItem("campaign_id");
  const queryClient = useQueryClient();

  const getKnowledgeBase = () => {
    return axios
      .get(`${baseUrl}/knowledgebase`, {
        headers: { Authorization: AuthStr },
      })
      .then((response) => {
        const data = response.data?.data;
        const options = data.map((base) => {
          return {
            label: base.document_name,
            value: base._id,
            query: base.knowledgebase_query,
            key: base._id,
          };
        });
        return options;
      })
      .catch((e) => {
        console.log(e);
        if (e?.response?.data?.statusCode === 401) navigate("/account/logout");
        return Promise.reject(
          new Error(e?.message ?? "Check your internet connection")
        );
      });
  };

  const finishCampaignCalls = () => {
    let {
      call_reason,
      call_goal,
      greetingType,
      greetings,
      qualification,
      script,
      knowledge_base,
    } = getValues();
    const greeting_after_pickup = greetingType;
    knowledge_base = knowledge_base?.map((base) => base?.value);
    const qualification_questions = qualification?.replace("\n", " ");
    const call_script = script?.replace("\n", " ");
    const greeting_message =
      greetingType === "greeting_myself" ? greetings?.replace("\n", " ") : "";
    call_reason = call_reason?.replace("\n", " ");

    if (campaign_id) {
      return axios
        .post(
          `${baseUrl}/campaigns/${campaign_id}/calls`,
          {
            call_reason,
            call_goal,
            greeting_after_pickup,
            greeting_message,
            qualification_questions,
            knowledge_base,
            call_script,
          },
          { headers: { Authorization: AuthStr } }
        )
        .then((response) => {
          const data = response.data;
          if (response?.status === 201) {
            const newpage = page + 1;
            const encryptedPage = CryptoJS.AES.encrypt(
              JSON.stringify(newpage),
              key!
            ).toString();
            sessionStorage.setItem("new_campaign_page", encryptedPage);
            setPage(page + 1);
            queryClient.invalidateQueries({ queryKey: [campaign?._id, token] });
          }
          return data;
        })
        .catch((e) => {
          console.log(e);
          if (e?.response?.data?.statusCode === 401)
            navigate("/account/logout");
          return Promise.reject(
            new Error(e?.message ?? "Check your internet connection")
          );
        });
    } else {
      sessionStorage.removeItem("new_campaign_page");
      return setPage(0);
    }
  };

  const {
    isLoading,
    isError,
    error,
    data: knowledgebase,
    refetch: refetchBase,
  } = useQuery({
    queryKey: ["getKnowledgebase", token],
    queryFn: () => getKnowledgeBase(),
    placeholderData: keepPreviousData,
  });

  const {
    isLoading: callLoading,
    error: callError,
    data: response,
    refetch,
  } = useQuery({
    queryKey: ["new_campaign_calls", token, campaign_id],
    queryFn: () => finishCampaignCalls(),
    enabled: false,
    retry: 0,
  });

  const handleNext = handleSubmit((data) => {
    refetch();
  });
  const options = [{ label: "Option", value: "option" }];
  const { campaignColorStyles: colorStyles } = useColorStyle(theme);

  useEffect(() => {
    if (!campaign || !knowledgebase) return;

    const {
      call_reason,
      call_goal,
      call_script,
      call_type,
      greeting_after_pickup,
      greeting_message,
      qualification_questions,
      knowledge_base,
    } = campaign;

    const allknowledgebase = knowledge_base
      ?.map((base) =>
        knowledgebase.find((document) => document?.value === base)
      )
      .filter(Boolean);

    reset({
      call_goal,
      call_reason,
      knowledge_base: allknowledgebase,
      script: call_script,
      greetingType: greeting_after_pickup,
      greetings: greeting_message,
      qualification: qualification_questions,
    });
  }, [campaign, knowledgebase]);

  useEffect(() => {
    if (!campaign || !knowledgebase) return;

    const currentKB = watch("knowledge_base");
    if (currentKB?.length) return; // don't override if already filled

    // proceed to reset if form is empty
  }, [campaign, knowledgebase]);

  return (
    <FormBox
      title={"Call details"}
      subtitle={"Input the info below to train the AI"}
      containerClass={
        "w-full text-secondary-dark font-switzer text-[12px] font-[400] leading-[15.84px] align-self-top self-top"
      }
      parentClass={"self-start md:w-[460px] mb-2"}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleNext();
        }}
        className="flex flex-col gap-[16px]"
        noValidate
      >
        <Textarea
          label={
            <div className="flex flex-row gap-[5px] items-center">
              <Text className={labelClassName}>Reason for call*</Text>
              <Tooltip
                rounded="sm"
                content={
                  <Text className="w-[220px] text-center text-[13px]">
                    Briefly explain why you're reaching out to the contact.
                  </Text>
                }
                className="font-switzer text-[12px] font-[400] !w-[150px] rounded-[8px]"
              >
                <Button
                  className="!p-0 !bg-transparent !m-0 !h-fit cursor-text disabled:border-none disabled:cursor-text"
                  disabled
                >
                  <BsFillInfoCircleFill size={12} className="mt-[0.7px]" />
                </Button>
              </Tooltip>
            </div>
          }
          variant="outline"
          rounded="md"
          style={{ resize: "none" }}
          rows={3}
          placeholder=""
          className="text-primary-dark resize-none"
          textareaClassName={textAreaClassName}
          labelClassName={labelClassName}
          {...register("call_reason", {
            ...projectFieldValidation,
          })}
          error={
            errors.call_reason?.message
              ? "This field " + errors.call_reason.message
              : ""
          }
        />

        <Input
          type="text"
          label={
            <div className="flex flex-row gap-[5px] items-center">
              <Text className={labelClassName}>Goal of the call*</Text>
              <Tooltip
                rounded="sm"
                content={
                  <Text className="w-[220px] text-center text-[13px]">
                    What you want to achieve by the end of the call. <br />
                    eg. Book an appointment
                  </Text>
                }
                className="font-switzer text-[12px] font-[400] !w-[150px] rounded-[8px]"
              >
                <Button
                  className="!p-0 !bg-transparent !m-0 !h-fit cursor-text disabled:border-none disabled:cursor-text"
                  disabled
                >
                  <BsFillInfoCircleFill size={12} className="mt-[0.7px]" />
                </Button>
              </Tooltip>
            </div>
          }
          variant="text"
          rounded="md"
          labelClassName={labelClassName}
          inputClassName={inputClassName}
          {...register("call_goal", {
            ...projectFieldValidation,
          })}
          error={
            errors.call_goal?.message
              ? "This field " + errors.call_goal.message
              : ""
          }
        />
        {/*         <Box>
          <Text className={cn(labelClassName, "mb-1.5")}>Reason for call</Text>
          <Controller
            control={control}
            name="call_reason"
            rules={{
              required: {
                value: true,
                message: "Required",
              },
            }}
            render={({ field: { onChange, value, name } }) => (
              <div>
                <CreatableSelect
                  options={options}
                  name={name}
                  placeholder=""
                  value={value}
                  styles={colorStyles}
                  onChange={onChange}
                />
                {errors.call_reason?.message && (
                  <p className="text-red text-[13px] mt-0.5 rizzui-textarea-error-text">
                    {"" + errors.call_reason.message}
                  </p>
                )}
              </div>
            )}
          />
        </Box>

        <Box>
          <Text className={cn(labelClassName, "mb-1.5")}>Goal of the call</Text>
          <Controller
            control={control}
            name="call_goal"
            rules={{
              required: {
                value: true,
                message: "Required",
              },
            }}
            render={({ field: { onChange, value, name } }) => (
              <div>
                <CreatableSelect
                  options={options}
                  name={name}
                  placeholder=""
                  value={value}
                  styles={colorStyles}
                  onChange={onChange}
                />
                {errors.call_goal?.message && (
                  <p className="text-red text-[13px] mt-0.5 rizzui-textarea-error-text">
                    {"" + errors.call_goal.message}
                  </p>
                )}
              </div>
            )}
          />
        </Box> */}
        <div className="flex flex-col gap-[16px]">
          <Text className={labelClassName}>Greetings after pickup*</Text>
          <div className="flex flex-col gap-[12px]">
            <Radio
              label="I’ll set the greeting myself"
              value="greeting_myself"
              size="sm"
              labelClassName={cn(
                labelClassName,
                "!text-primary-dark ml-[16px]"
              )}
              {...register("greetingType", {
                ...projectFieldValidation,
              })}
            />

            <Radio
              label="I want it to be dynamic"
              size="sm"
              value="greeting_dynamic"
              labelClassName={cn(
                labelClassName,
                "!text-primary-dark ml-[16px]"
              )}
              {...register("greetingType", {
                ...projectFieldValidation,
              })}
            />
          </div>

          {errors.greetingType?.message && (
            <p className="text-red text-[13px]">{"Select an option"}</p>
          )}
        </div>

        {watch("greetingType") === "greeting_myself" && (
          <Textarea
            label="Type the greetings here*"
            variant="outline"
            rounded="md"
            style={{ resize: "none" }}
            rows={3}
            placeholder="E.g: Hi Kelvin, this is Sarah from RingConnect! how are you doing today, I'd like just one minute of your time"
            className="text-primary-dark resize-none"
            textareaClassName={textAreaClassName}
            labelClassName={labelClassName}
            {...register("greetings", {
              ...projectFieldValidation,
            })}
            error={
              errors.greetings?.message
                ? "This field " + errors.greetings.message
                : ""
            }
          />
        )}

        <Textarea
          label="What are your qualification questions*"
          variant="outline"
          rounded="md"
          rows={3}
          style={{ resize: "none" }}
          className="text-primary-dark resize-none"
          labelClassName={labelClassName}
          textareaClassName={textAreaClassName}
          {...register("qualification", {
            ...projectFieldValidation,
          })}
          error={
            errors.qualification?.message
              ? "This field " + errors.qualification.message
              : ""
          }
        />

        <Textarea
          label="Provide a script"
          variant="outline"
          rounded="md"
          rows={3}
          textareaClassName={textAreaClassName}
          labelClassName={labelClassName}
          {...register("script", {
            required: {
              value: false,
              message: "This field is required",
            },
          })}
          style={{ resize: "none" }}
          error={errors.script?.message ? " " + errors.script.message : ""}
        />
        <Controller
          control={control}
          name="knowledge_base"
          rules={{
            required: {
              value: false,
              message: "Select at least one file",
            },
          }}
          render={({ field: { onChange, value, name } }) => (
            <div className="customBar overflow-y-auto border border-[#758599] border-dashed rounded-[8px] flex flex-col items-start h-fit max-h-[218px] px-[16px] py-[15px] gap-[8px]">
              <div className="mb-1.5 justify-between flex w-full">
                <div className="flex flex-row gap-[5px] items-center">
                  <Text className={labelClassName}>Knowledge base</Text>
                  <Tooltip
                    rounded="sm"
                    content={
                      <Text className="w-[220px] text-center text-[13px]">
                        A knowledge base is a collection of resources that your
                        AI assistant can easily access for information.
                      </Text>
                    }
                    className="font-switzer text-[12px] font-[400] !w-[150px] rounded-[8px]"
                  >
                    <Button
                      className="!p-0 !bg-transparent !m-0 !h-fit cursor-text disabled:border-none disabled:cursor-text"
                      disabled
                    >
                      <BsFillInfoCircleFill size={12} className="mt-[1px]" />
                    </Button>
                  </Tooltip>
                </div>

                <Text
                  onClick={() =>
                    openModal({
                      view: (
                        <ImportMultipleFiles
                          page="projects"
                          setUploaded={refetchBase}
                        />
                      ),
                    })
                  }
                  className="cursor-pointer underline font-switzer font-[500] text-[12px] leading-[15.84px] text-primary-dark"
                >
                  Upload a new file
                </Text>
              </div>

              <div className="flex flex-col gap-[8px] w-full">
                {watch("knowledge_base")?.map((base, i) => (
                  <div
                    key={i}
                    className="border border-[#D9D9D9] rounded-[4px] flex flex-col h-[60px] w-full p-[13px]"
                  >
                    <div className="w-full flex place-content-center place-items-center justify-between overflow-hidden">
                      <div className="flex w-full flex-col gap-[6px]">
                        <Text className="font-switzer text-[12px] truncate text-primary-dark">
                          {base?.label}
                        </Text>
                        <Text className="font-switzer text-[12px] truncate text-[#758599]">
                          {base?.query}
                        </Text>
                      </div>
                      <div className="flex flex-row gap-1">
                        <ActionIcon
                          variant="text"
                          onClick={() => {
                            const filtered = value.filter(
                              (_, index) => index !== i
                            );
                            onChange(filtered);
                            if (filtered.length === 0) {
                              setShowDropdown(true); // Show dropdown if no items left
                            }
                          }}
                          className="size-[17px] !p-[0px]"
                        >
                          <BinIcon className="inline-block" />
                        </ActionIcon>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Toggleable dropdown */}
              {showDropdown ? (
                <Select
                  options={knowledgebase}
                  isMulti
                  className={cn("w-full")}
                  isClearable
                  placeholder="Select file..."
                  isSearchable={false}
                  isLoading={isLoading}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    ...colorStyles,
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }), // ensure it's on top
                  }}
                  value={value}
                  name={name}
                  onChange={(selected) => {
                    if (
                      selected?.some(
                        (option) => option.value === "upload_new_file"
                      )
                    ) {
                      openModal({
                        view: (
                          <ImportMultipleFiles
                            page="projects"
                            setUploaded={refetchBase}
                          />
                        ),
                      });
                      const filtered = selected.filter(
                        (option) => option.value !== "upload_new_file"
                      );
                      onChange(filtered);
                    } else {
                      onChange(selected);
                    }
                    if (
                      !selected?.some(
                        (option) => option.value === "upload_new_file"
                      )
                    )
                      setShowDropdown(false); // Hide after selection
                  }}
                />
              ) : (
                <Button
                  type="button"
                  onClick={() => setShowDropdown(true)}
                  className="text-[12px] text-primary-dark font-[500] font-switzer m-0 p-0 h-fit !bg-transparent"
                >
                  + Add another knowledge base
                </Button>
              )}

              {errors.knowledge_base?.message && (
                <p className="text-red text-[13px] mt-0.5 rizzui-textarea-error-text">
                  {"" + errors.knowledge_base.message}
                </p>
              )}
            </div>
          )}
        />

        <div className="flex justify-between gap-[26px]">
          <Button
            onClick={() => {
              setCurrentId(campaign_id);
              const newpage = page - 1;
              const encryptedPage = CryptoJS.AES.encrypt(
                JSON.stringify(newpage),
                key!
              ).toString();
              sessionStorage.setItem("new_campaign_page", encryptedPage);
              setPage(newpage);
            }}
            className="h-[40px] !bg-[#F2F2F2] dark:!bg-muted dark:!text-[#D9D9D9] w-[119px] !rounded-[4px] font-[500] font-switzer text-[12px] leading-[15.84px] text-[#191919]"
          >
            Go back
          </Button>

          <Button
            type="submit"
            isLoading={callLoading}
            className="h-[40px] w-[251px] !bg-btn !rounded-[4px] font-[500] font-switzer text-[12px] leading-[15.84px] text-background"
          >
            Continue
          </Button>
        </div>
      </form>
      {callError && (
        <Text className="text-[red] font-switzer mt-2 text-[12px] font-[400]">
          {callError.message}
        </Text>
      )}
    </FormBox>
  );
}
